import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Video, Plus, Trash2, X, Upload, Link as LinkIcon, Play, ArrowUp, ArrowDown, Eye, EyeOff, ImageIcon, Loader2 } from "lucide-react";

export default function AdminVideos() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [thumbing, setThumbing] = useState(null); // video_id currently being processed

  const load = () => axios.get(`${API}/admin/landing-videos`).then(r => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggle = async (v) => { await axios.patch(`${API}/admin/landing-videos/${v.video_id}`, { is_active: !v.is_active }); load(); };
  const remove = async (v) => { if (!window.confirm(`Delete "${v.title}"?`)) return; await axios.delete(`${API}/admin/landing-videos/${v.video_id}`); load(); };
  const reorder = async (v, delta) => { await axios.patch(`${API}/admin/landing-videos/${v.video_id}`, { display_order: (v.display_order || 0) + delta }); load(); };

  // Re-generate a poster thumbnail from the playback source. Works for both
  // uploaded base64 videos and direct-URL videos. Has no effect on
  // YouTube / Vimeo (those use the hosted poster).
  const generateThumb = async (v) => {
    setThumbing(v.video_id);
    try {
      // Pull the heavy playback payload only when this admin action runs.
      const pb = await axios.get(`${API}/landing-videos/${v.video_id}/playback`).then(r => r.data);
      const src = pb.video_base64 || pb.video_url;
      if (!src || pb.source === "youtube" || pb.source === "vimeo") {
        alert("Thumbnail auto-generation only works for uploaded / direct-mp4 videos. YouTube & Vimeo use their hosted posters.");
        return;
      }
      const poster = await new Promise((resolve, reject) => {
        const vid = document.createElement("video");
        vid.crossOrigin = "anonymous";
        vid.muted = true;
        vid.playsInline = true;
        vid.preload = "metadata";
        vid.src = src;
        vid.onloadeddata = () => { try { vid.currentTime = Math.min(0.5, (vid.duration || 1) * 0.05); } catch { /* ignore */ } };
        vid.onseeked = () => {
          try {
            const w = vid.videoWidth || 640, h = vid.videoHeight || 360;
            const scale = Math.min(1, 1024 / w);
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(w * scale);
            canvas.height = Math.round(h * scale);
            canvas.getContext("2d").drawImage(vid, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.78));
          } catch (err) { reject(err); }
        };
        vid.onerror = () => reject(new Error("video load failed"));
      });
      await axios.patch(`${API}/admin/landing-videos/${v.video_id}`, { poster_url: poster });
      load();
    } catch (err) {
      alert("Could not generate thumbnail: " + (err?.message || "unknown error"));
    } finally {
      setThumbing(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-videos-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">Landing Page · Promo Videos</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Showcase your videos</h1>
          <p className="text-ink-soft text-sm max-w-xl">Upload promotional animations or paste YouTube/Vimeo links. They'll appear on the landing page in the order you set below.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="add-video-btn" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> Add video
        </button>
      </header>
      <div className="bg-sky/10 border border-sky/40 rounded-2xl px-4 py-3 text-xs text-ink-soft flex items-start gap-2">
        <Eye className="w-4 h-4 text-sky shrink-0 mt-0.5"/>
        <div>
          <strong className="text-ink">Where do they appear?</strong> All <em>active</em> videos show on the public landing page video showcase. The one tagged <code className="bg-surface-muted rounded px-1.5 py-0.5 text-[11px] font-mono">main</code> is featured as the hero; the rest appear in the carousel. Reorder with the arrows below.
          <br/>
          <strong className="text-ink">Missing a thumbnail?</strong> Click the <ImageIcon className="w-3 h-3 inline-block"/> icon on a video card to auto-extract the first frame as the poster image. New uploads get this automatically.
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(v => (
          <article key={v.video_id} data-testid={`video-${v.video_id}`} className={`bg-surface border rounded-3xl overflow-hidden shadow-soft transition-all ${v.is_active ? "border-line" : "border-dashed border-line opacity-60"}`}>
            <div className="relative aspect-video bg-surface-muted">
              {v.thumbnail ? (
                <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover"/>
              ) : (v.source === "upload" && v.video_base64) ? (
                <video src={v.video_base64} muted playsInline preload="metadata" className="w-full h-full object-cover"/>
              ) : v.video_url ? (
                /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/.test(v.video_url) ? (
                  <img src={`https://img.youtube.com/vi/${(v.video_url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/) || [])[1] || ""}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                ) : (
                  <video src={v.video_url} muted playsInline preload="metadata" className="w-full h-full object-cover"/>
                )
              ) : (
                <div className="w-full h-full grid place-items-center text-ink-soft"><Video className="w-10 h-10 opacity-30"/></div>
              )}
              <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-all grid place-items-center opacity-0 hover:opacity-100"><Play className="w-10 h-10 text-white"/></div>
              <span className="absolute top-2 left-2 bg-black/70 text-white rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{v.source || "link"}</span>
            </div>
            <div className="p-4">
              <h3 className="font-heading text-lg font-extrabold line-clamp-1">{v.title}</h3>
              <p className="text-xs text-ink-soft mt-1 line-clamp-2">{v.description || "No description"}</p>
              <div className="flex items-center gap-2 mt-3 text-xs text-ink-soft">
                <span className="font-bold">#{v.display_order || 0}</span>
                <span className="bg-surface-muted rounded-full px-2 py-0.5 font-bold">{v.section_tag || "main"}</span>
              </div>
              <div className="flex items-center gap-1 mt-3 border-t border-line pt-3">
                <button onClick={() => reorder(v, -1)} title="Move up" className="text-ink-soft hover:text-brand p-1.5"><ArrowUp className="w-3.5 h-3.5"/></button>
                <button onClick={() => reorder(v, 1)} title="Move down" className="text-ink-soft hover:text-brand p-1.5"><ArrowDown className="w-3.5 h-3.5"/></button>
                <button data-testid={`toggle-${v.video_id}`} onClick={() => toggle(v)} title={v.is_active ? "Hide on landing" : "Show on landing"} className="text-ink-soft hover:text-brand p-1.5">{v.is_active ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}</button>
                <button
                  data-testid={`thumb-${v.video_id}`}
                  onClick={() => generateThumb(v)}
                  disabled={thumbing === v.video_id}
                  title="Auto-generate thumbnail from first frame"
                  className="text-ink-soft hover:text-brand p-1.5 disabled:opacity-50"
                >
                  {thumbing === v.video_id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ImageIcon className="w-3.5 h-3.5"/>}
                </button>
                <button onClick={() => setEditing(v)} className="ml-auto text-brand text-xs font-bold">Edit</button>
                <button data-testid={`delete-${v.video_id}`} onClick={() => remove(v)} title="Delete" className="text-ink-soft hover:text-brand p-1.5"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {items.length === 0 && (
        <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">
          <Video className="w-10 h-10 mx-auto opacity-40 mb-2"/>
          No videos yet. Click "Add video" to upload your first promo.
        </div>
      )}
      {creating && <VideoModal onClose={() => { setCreating(false); load(); }}/>}
      {editing && <VideoModal video={editing} onClose={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function VideoModal({ video, onClose }) {
  const isEdit = !!video;
  const [mode, setMode] = useState(video?.source === "upload" ? "upload" : "url");
  const [form, setForm] = useState(video || { title: "", description: "", video_url: "", video_base64: null, poster_url: "", section_tag: "main", display_order: 0, is_active: true });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Extract the first visible frame from a video (data URL or remote URL) and
  // return it as a JPEG data URL. The landing-page listing endpoint never
  // ships the heavy base64 blob anymore (see `/api/landing-videos`), so we
  // pre-render a thumbnail here at upload time and persist it as `poster_url`.
  const _capturePosterFromSrc = (src) => new Promise((resolve, reject) => {
    try {
      const vid = document.createElement("video");
      vid.crossOrigin = "anonymous";
      vid.muted = true;
      vid.playsInline = true;
      vid.preload = "metadata";
      vid.src = src;
      const done = () => {
        try {
          const w = vid.videoWidth || 640;
          const h = vid.videoHeight || 360;
          const maxW = 1024;
          const scale = Math.min(1, maxW / w);
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          canvas.getContext("2d").drawImage(vid, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.78));
        } catch (err) { reject(err); }
      };
      vid.onloadeddata = () => {
        // Seek a tiny bit in to avoid black first frames.
        try { vid.currentTime = Math.min(0.5, (vid.duration || 1) * 0.05); } catch { done(); }
      };
      vid.onseeked = done;
      vid.onerror = () => reject(new Error("Could not load video for poster extraction"));
    } catch (err) { reject(err); }
  });

  const onFile = (file) => {
    if (!file) return;
    setError("");
    if (file.size > 25 * 1024 * 1024) { setError("File must be under 25 MB. Use YouTube/Vimeo for larger files."); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async e => {
      const dataUrl = e.target.result;
      set("video_base64", dataUrl);
      set("video_url", "");
      // Auto-capture poster frame so the public landing thumbnail isn't blank.
      // We don't block the upload on failure here — the admin can paste a
      // poster_url manually if extraction fails (e.g. unsupported codec).
      try {
        if (!form.poster_url) {
          const poster = await _capturePosterFromSrc(dataUrl);
          set("poster_url", poster);
        }
      } catch (err) {
        // Silent fail — poster_url stays empty and the gradient placeholder is shown.
        console.warn("Poster extraction failed:", err?.message);
      }
      setUploading(false);
    };
    reader.onerror = () => { setError("Could not read file"); setUploading(false); };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form };
      if (mode === "url") { payload.video_base64 = null; }
      else { payload.video_url = ""; }
      if (isEdit) {
        await axios.patch(`${API}/admin/landing-videos/${video.video_id}`, payload);
      } else {
        await axios.post(`${API}/admin/landing-videos`, payload);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save video");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-lg border border-line shadow-soft max-h-[90vh] overflow-y-auto" data-testid="video-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-extrabold">{isEdit ? "Edit video" : "Add a video"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="bg-surface-muted/60 rounded-full p-1 flex gap-1 mb-4">
          <button type="button" data-testid="mode-url" onClick={() => setMode("url")} className={`flex-1 rounded-full py-2 text-sm font-bold inline-flex items-center justify-center gap-1.5 ${mode === "url" ? "bg-ink text-white" : "text-ink-soft"}`}>
            <LinkIcon className="w-4 h-4"/> URL (YouTube · Vimeo · MP4)
          </button>
          <button type="button" data-testid="mode-upload" onClick={() => setMode("upload")} className={`flex-1 rounded-full py-2 text-sm font-bold inline-flex items-center justify-center gap-1.5 ${mode === "upload" ? "bg-ink text-white" : "text-ink-soft"}`}>
            <Upload className="w-4 h-4"/> Upload (≤25 MB)
          </button>
        </div>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Title *</span>
          <input data-testid="video-title" required value={form.title} onChange={e => set("title", e.target.value)} className="inp"/>
        </label>
        <label className="block mt-3">
          <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Description (optional)</span>
          <textarea data-testid="video-desc" value={form.description || ""} onChange={e => set("description", e.target.value)} rows={2} className="inp"/>
        </label>
        {mode === "url" && (
          <label className="block mt-3">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Video URL *</span>
            <input data-testid="video-url" required={mode === "url" && !isEdit} value={form.video_url || ""} onChange={e => set("video_url", e.target.value)} placeholder="https://www.youtube.com/watch?v=... or .mp4 link" className="inp"/>
            <div className="text-[11px] text-ink-soft mt-1">YouTube + Vimeo links auto-detect thumbnails.</div>
          </label>
        )}
        {mode === "upload" && (
          <label className="block mt-3">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Upload video (MP4 / WebM, ≤25 MB)</span>
            <div className="mt-1 border-2 border-dashed border-line rounded-2xl p-6 text-center hover:bg-surface-muted/50 transition-all cursor-pointer">
              <input data-testid="video-file" type="file" accept="video/mp4,video/webm,video/quicktime" onChange={e => onFile(e.target.files?.[0])} className="hidden"/>
              <Upload className="w-6 h-6 mx-auto text-ink-soft"/>
              <div className="text-sm font-bold mt-2">{uploading ? "Reading file…" : form.video_base64 ? "✓ Video selected" : "Click to choose file"}</div>
              <div className="text-[11px] text-ink-soft mt-1">For larger videos, host on YouTube/Vimeo & paste the URL.</div>
            </div>
          </label>
        )}
        <label className="block mt-3">
          <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Custom poster image URL (optional)</span>
          <input value={form.poster_url || ""} onChange={e => set("poster_url", e.target.value)} placeholder="https://…" className="inp"/>
        </label>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Section</span>
            <select value={form.section_tag || "main"} onChange={e => set("section_tag", e.target.value)} className="inp">
              <option value="main">Main showcase</option>
              <option value="rescue">Rescue stories</option>
              <option value="vet">Vet stories</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Display order</span>
            <input type="number" value={form.display_order ?? 0} onChange={e => set("display_order", Number(e.target.value))} className="inp"/>
          </label>
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm">
          <input type="checkbox" checked={form.is_active !== false} onChange={e => set("is_active", e.target.checked)} data-testid="video-active"/>
          Show on landing page
        </label>
        {error && <p className="text-sm text-brand mt-3 bg-brand/10 rounded-2xl p-3">{error}</p>}
        <button data-testid="save-video" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold">{isEdit ? "Save changes" : "Add to landing"}</button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;margin-top:0.25rem;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}
